import React from "react";
import styled from "styled-components";
import { Formik, FieldArray, Field } from "formik";
import {
  ModalContent,
  ModalOverlay,
  HeadingContainer,
  HeaderText,
  Heading,
  FormContainer,
  ButtonContainer,
} from "../../ClaimItemsListModal/GenerateInvoice";
import { FormLabel, Modal } from "@material-ui/core";
import { PrimaryCTAButton, PrimaryOutlinedCloseCTAButton } from "../../Buttons";
import { CustomInputField } from "../../FormInputs";
import theme from "../../../../assets/theme";
import { respondentValidationSchema } from "../../../../helpers/validationSchema";
import { CloseBtn } from "../../../internal/BulkUpdate";
import AddIcon from "@material-ui/icons/Add";
import CaseService from "../../../../services/CaseService";
import { useSnackbar } from "notistack";
import { getErrorMessage } from "../../../../helpers/functions";
import useLoader from "../../../../hooks/useLoader";

const TrashIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4h6v2" />
  </svg>
);

function findDuplicateEmailIndices(parties) {
  const emailMap = {};
  const duplicates = new Set();
  parties.forEach(({ email }, idx) => {
    if (!email) return;
    const key = email.trim().toLowerCase();
    if (emailMap[key] !== undefined) {
      duplicates.add(emailMap[key]);
      duplicates.add(idx);
    } else {
      emailMap[key] = idx;
    }
  });
  return duplicates;
}

function RespondentPartyCard({
  index,
  values,
  errors,
  touched,
  setFieldValue,
  remove,
  canRemove,
  isDuplicateEmail,
}) {
  const err = errors.respondentParties?.[index] || {};
  const tch = touched.respondentParties?.[index] || {};

  return (
    <RespondentCard>
      <CardHeader>
        <CardIndex>
          <IndexBadge>{String(index + 1).padStart(2, "0")}</IndexBadge>
        </CardIndex>
        <RemoveBtn
          type="button"
          onClick={() => remove(index)}
          disabled={!canRemove}
          title="Remove respondent"
        >
          <TrashIcon />
        </RemoveBtn>
      </CardHeader>

      <FieldRow>
        <FieldGroup>
          <FormLabel
            style={{
              fontFamily: "openSans-SemiBold, sans-serif",
              fontSize: 12,
              color: "#949ab0",
            }}
          >
            {" "}
            Name <span className="required-star"> *</span>
          </FormLabel>

          <Field name={`respondentParties[${index}].name`}>
            {({ field }) => (
              <CustomInputField
                {...field}
                id={`name-${index}`}
                variant="outlined"
                error={err.name && tch.name}
                helperText={tch.name && err.name}
              />
            )}
          </Field>
        </FieldGroup>

        <FieldGroup>
          <FormLabel
            style={{
              fontFamily: "openSans-SemiBold, sans-serif",
              fontSize: 12,
              color: "#949ab0",
            }}
          >
            Email <span className="required-star"> *</span>
          </FormLabel>
          <Field name={`respondentParties[${index}].email`}>
            {({ field }) => (
              <CustomInputField
                {...field}
                id={`email-${index}`}
                variant="outlined"
                type="email"
                placeholder="email@example.com"
                error={(err.email && tch.email) || isDuplicateEmail}
                helperText={
                  isDuplicateEmail
                    ? "Duplicate email — each respondent must have a unique email"
                    : tch.email && err.email
                }
              />
            )}
          </Field>
        </FieldGroup>
      </FieldRow>

      <FieldRow style={{ marginTop: 12 }}>
        <FieldGroup>
          <FormLabel
            style={{
              fontFamily: "openSans-SemiBold, sans-serif",
              fontSize: 12,
              color: "#949ab0",
            }}
          >
            Mobile
          </FormLabel>
          <Field name={`respondentParties[${index}].mobile`}>
            {({ field }) => (
              <CustomInputField
                {...field}
                variant="outlined"
                id={`mobile-${index}`}
                placeholder="00000 00000"
              />
            )}
          </Field>
        </FieldGroup>
        <FieldGroup>
          <FormLabel
            style={{
              fontFamily: "openSans-SemiBold, sans-serif",
              fontSize: 12,
              color: "#949ab0",
            }}
          >
            Address
          </FormLabel>
          <Field name={`respondentParties[${index}].address`}>
            {({ field }) => (
              <CustomInputField
                {...field}
                variant="outlined"
                id={`address-${index}`}
                placeholder="City, State, Country"
              />
            )}
          </Field>
        </FieldGroup>
      </FieldRow>
    </RespondentCard>
  );
}

const defaultParty = () => ({
  name: "",
  email: "",
  mobile: "",
  address: "",
  respondentKind: "individual",
});

const initialValues = {
  respondentParties: [
    {
      name: "",
      email: "",
      mobile: "",
      address: "",
      respondentKind: "individual",
    },
  ],
};

export default function RespondentPartiesModal({
  open,
  setOpen,
  respondentParties,
  id,
  setRefreshDetails,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();

  async function onFormSubmit(values) {
    const dupes = findDuplicateEmailIndices(values.respondentParties);
    if (dupes.size > 0) {
      return;
    }
    try {
      setLoader({ state: true, message: "genrate invoice..." });
      let payload = {
        respondentParties: [...respondentParties, ...values.respondentParties],
      };
      const res = await CaseService.updateCase(id, payload);
      if (res) {
        enqueueSnackbar(res.message, {
          variant: "success",
        });
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
      setOpen(false);
      setRefreshDetails(true);
    }
  }

  return (
    <>
      <Modal open={open} onClose={() => setOpen(false)} disableRestoreFocus>
        <DrawerContainer>
          <ModalOverlay />
          <ModalContent>
            <HeadingContainer>
              <HeaderLeft>
                <HeaderText>
                  <Heading>Respondent Parties</Heading>
                </HeaderText>
              </HeaderLeft>
              <CloseBtn onClick={() => setOpen(false)} aria-label="Close">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </CloseBtn>
            </HeadingContainer>

            <Formik
              initialValues={initialValues}
              enableReinitialize
              validateOnBlur
              validationSchema={respondentValidationSchema}
              validateOnChange
              onSubmit={onFormSubmit}
            >
              {({
                values,
                handleChange,
                errors,
                handleBlur,
                touched,
                handleSubmit,
                setFieldValue,
                isSubmitting,
              }) => {
                const duplicates = findDuplicateEmailIndices(
                  values.respondentParties,
                );
                const hasDuplicates = duplicates.size > 0;

                return (
                  <>
                    <FormContainer>
                      <FieldArray name="respondentParties">
                        {({ remove, push }) => (
                          <>
                            {values.respondentParties.map((_, index) => (
                              <RespondentPartyCard
                                key={index}
                                index={index}
                                values={values}
                                errors={errors}
                                touched={touched}
                                setFieldValue={setFieldValue}
                                remove={remove}
                                canRemove={values.respondentParties.length > 1}
                                isDuplicateEmail={duplicates.has(index)}
                              />
                            ))}
                            <div className="add_container">
                              <div
                                className="add_circle"
                                onClick={() => push(defaultParty())}
                              >
                                <span>Add Another Respondent</span>
                                <AddIcon className="add_icon" />
                              </div>
                            </div>
                          </>
                        )}
                      </FieldArray>
                    </FormContainer>
                    <ButtonContainer>
                      <PrimaryOutlinedCloseCTAButton
                        style={{ width: "auto" }}
                        onClick={() => setOpen(false)}
                      >
                        Cancel
                      </PrimaryOutlinedCloseCTAButton>
                      <PrimaryCTAButton
                        style={{ width: "auto" }}
                        disabled={isSubmitting || hasDuplicates}
                        onClick={handleSubmit}
                      >
                        Submit
                      </PrimaryCTAButton>
                    </ButtonContainer>
                  </>
                );
              }}
            </Formik>
          </ModalContent>
        </DrawerContainer>
      </Modal>
    </>
  );
}

const DrawerContainer = styled.div`
  max-width: 95%;
  width: 100%;
  outline: none;
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 8px;
  transform: translate(-50%, -50%);
  @media ${theme?.breakpoints?.sm_up} {
    max-width: 60%;
  }
  & .add_container {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
  }

  & .add_circle {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border-radius: 24px;
    cursor: pointer;
    background-color: rgba(0, 131, 140, 0.13);
    transition: all 0.25s ease;
  }

  & .add_circle span {
    color: #00838c;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
  }

  & .add_icon {
    color: #00838c;
    font-size: 18px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RespondentCard = styled.div`
  border: 1px solid #e1e3ee;
  border-radius: 14px;
  padding: 20px;
  position: relative;
  margin-bottom: 10px;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const CardIndex = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const IndexBadge = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #00838c;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  font-family: openSans-SemiBold, sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(108, 99, 255, 0.25);
`;

const RemoveBtn = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid transparent;
  background: transparent;
  color: #7a7a9a;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  &:hover {
    background: rgba(255, 77, 109, 0.1);
    color: #ff4d6d;
    border-color: rgba(255, 77, 109, 0.3);
  }
  svg {
    width: 15px;
    height: 15px;
  }
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 8px;
`;
